import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbRefresh } from "react-icons/tb";
import { ArrowDownward, HourglassBottom, Report, WarningAmber } from "@mui/icons-material";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { baasProxy } from "../../../../Services/baasConsoleClient";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import { paginateList } from "../../Extract/extractFilters";
import { hasBaasBankingRails } from "../baasCapabilities";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { BaasRowActionMenu } from "../components/BaasRowActionMenu";
import {
  GRID_HISTORY_COLS_8,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryDetailButton,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { BaasPldDetailDrawer } from "./components/BaasPldDetailDrawer";

const STATUS_TABS = [
  { id: "pending", labelKey: "baas.pld.status.pending", fallback: "Pendentes" },
  { id: "approved", labelKey: "baas.pld.status.approved", fallback: "Aprovados" },
  { id: "rejected", labelKey: "baas.pld.status.rejected", fallback: "Rejeitados" },
  { id: "all", labelKey: "baas.pld.tabs.all", fallback: "Todos" },
];

const REASON_FILTERS = [
  { id: "", label: "Todos motivos" },
  { id: "limit_exceeded", label: "Limite excedido" },
  { id: "fraud_suspected", label: "Fraude suspeita" },
  { id: "high_value_inbound", label: "Entrada alto valor" },
];

const fromMajor = (reais) => {
  const n = Number(reais);
  if (!Number.isFinite(n)) return "—";
  return formatMonetaryValue(n, "BRL");
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const metricAmount = (metric) => {
  const n = Number(metric?.amount);
  return Number.isFinite(n) ? n : 0;
};

const intlOr = (key, fallback) => {
  const value = intl.get(key);
  return value && value !== key ? value : fallback;
};

const SummaryMetric = ({ label, value, sublabel, icon }) => (
  <div className="extract-period-summary__metric summary-card min-w-[140px] flex-1 shrink-0">
    <div className="flex min-w-0 items-center gap-2">
      <div className="summary-card-icon">{icon}</div>
      <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">{label}</p>
    </div>
    <p className="mt-2 break-words text-lg font-bold leading-tight tabular-nums text-brand-dark dark:text-gray-100 sm:text-xl">
      {value}
    </p>
    {sublabel ? (
      <p className="mt-1 text-[0.7rem] leading-snug text-gray-400 dark:text-gray-500">{sublabel}</p>
    ) : null}
  </div>
);

const PldStatusBadge = ({ status }) => {
  const key = String(status || "pending").toLowerCase();
  const styles = {
    pending: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    approved: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    rejected: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[key] ?? styles.pending}`}
    >
      {intlOr(`baas.pld.status.${key}`, key)}
    </span>
  );
};

const ReasonBadge = ({ reason }) => {
  const key = String(reason || "").trim().toLowerCase();
  const styles = {
    limit_exceeded: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    fraud_suspected: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    high_value_inbound: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  };
  const labels = {
    limit_exceeded: "Limite excedido",
    fraud_suspected: "Fraude suspeita",
    high_value_inbound: "Alto valor",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[key] ?? "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300"}`}
    >
      {labels[key] ?? reason ?? "—"}
    </span>
  );
};

/**
 * Console PLD — holds blocked by limit exceed (PIX / stables saque).
 */
export const BaasPldPage = () => {
  const { selectedInstance, dataEpoch } = useBaasInstance();
  const { addToast } = useToast();
  const { can, capabilities, loading: capsLoading } = useBaasCapabilities();
  const isLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${dataEpoch}` : null,
  );
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [railTab, setRailTab] = useState("pix");
  const [statusTab, setStatusTab] = useState("pending");
  const [reasonFilter, setReasonFilter] = useState("");
  const [detailItem, setDetailItem] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const canAct = can("settings.write") || can("console.write");
  const showPage =
    hasBaasBankingRails(capabilities) || capabilities?.products?.stables?.enabled === true;

  const refresh = useCallback(async () => {
    if (!selectedInstance || !showPage) {
      setItems([]);
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const query = { rail: railTab };
      if (statusTab !== "all") query.status = statusTab;
      if (reasonFilter) query.reason_code = reasonFilter;

      const data = await baasProxy({
        instance: selectedInstance,
        method: "GET",
        baasPath: "v1/pld/holds",
        query,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary ?? null);
    } catch (error) {
      setItems([]);
      setSummary(null);
      addToast({
        type: "error",
        title: error?.message || intlOr("baas.pld.loadError", "Falha ao carregar PLD."),
      });
    } finally {
      setLoading(false);
    }
  }, [selectedInstance, showPage, railTab, statusTab, reasonFilter, addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setPage(1);
  }, [railTab, statusTab, reasonFilter, selectedInstance?.id]);

  const paginatedItems = useMemo(
    () => paginateList(items, page, rowsPerPage),
    [items, page, rowsPerPage],
  );

  const act = async (id, action) => {
    if (!selectedInstance || actingId) return;
    setActingId(id);
    try {
      await baasProxy({
        instance: selectedInstance,
        method: "POST",
        baasPath: `v1/pld/holds/${encodeURIComponent(id)}/${action}`,
        body: {},
      });
      addToast({
        type: "success",
        title:
          action === "approve"
            ? intlOr("baas.pld.approveSuccess", "Hold aprovado.")
            : intlOr("baas.pld.rejectSuccess", "Hold rejeitado."),
      });
      await refresh();
      setDetailItem(null);
    } catch (error) {
      addToast({
        type: "error",
        title: error?.message || intlOr("baas.pld.actionError", "Falha na ação."),
      });
    } finally {
      setActingId(null);
    }
  };

  if (isLoading || capsLoading) {
    return <BaasTablePageSkeleton columns={8} />;
  }

  if (!showPage) {
    return (
      <div className="baas-pld min-w-0 space-y-5">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intlOr("baas.nav.pld", "PLD")}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intlOr("baas.pld.empty", "Sem holds PLD para esta instância.")}
        </p>
      </div>
    );
  }

  return (
    <div className="baas-pld min-w-0 space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intlOr("baas.pld.title", "PLD — limites e bloqueios")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intlOr("baas.pld.subtitle", "Holds aguardando liberação ou revisão.")}
          </p>
        </div>
        <button
          type="button"
          className="btn-outline w-auto shrink-0"
          onClick={refresh}
          disabled={loading}
        >
          <TbRefresh className="h-4 w-4" aria-hidden />
          {intlOr("baas.pld.refresh", "Atualizar")}
        </button>
      </header>

      <div
        className="extract-period-summary extract-period-summary--with-pending"
        role="region"
        aria-label={intlOr("baas.pld.totalsLabel", "Totais PLD")}
      >
        <SummaryMetric
          label={intlOr("baas.pld.cards.limitExceeded", "Limite excedido")}
          value={fromMajor(metricAmount(summary?.limit_exceeded ?? summary?.limitExceeded))}
          sublabel={`${summary?.limit_exceeded?.count ?? summary?.limitExceeded?.count ?? 0}`}
          icon={<Report sx={{ fontSize: 16 }} aria-hidden />}
        />
        <SummaryMetric
          label={intlOr("baas.pld.cards.fraudSuspected", "Fraude suspeita")}
          value={fromMajor(metricAmount(summary?.fraud_suspected ?? summary?.fraudSuspected))}
          sublabel={`${summary?.fraud_suspected?.count ?? summary?.fraudSuspected?.count ?? 0}`}
          icon={<WarningAmber sx={{ fontSize: 16 }} aria-hidden />}
        />
        <SummaryMetric
          label={intlOr("baas.pld.cards.highValueInbound", "Entrada alto valor")}
          value={fromMajor(metricAmount(summary?.high_value_inbound ?? summary?.highValueInbound))}
          sublabel={`${summary?.high_value_inbound?.count ?? summary?.highValueInbound?.count ?? 0}`}
          icon={<ArrowDownward sx={{ fontSize: 16 }} aria-hidden />}
        />
        <SummaryMetric
          label={intlOr("baas.pld.cards.pixOverLimit", "Pix acima do limite")}
          value={fromMajor(metricAmount(summary?.pix_over_limit ?? summary?.pixOverLimit))}
          sublabel={`${summary?.pix_over_limit?.count ?? summary?.pixOverLimit?.count ?? 0}`}
          icon={<Report sx={{ fontSize: 16 }} aria-hidden />}
        />
        <SummaryMetric
          label={intlOr("baas.pld.cards.stablesWithdraw", "Stables saque")}
          value={fromMajor(metricAmount(summary?.stables_withdraw ?? summary?.stablesWithdraw))}
          sublabel={`${summary?.stables_withdraw?.count ?? summary?.stablesWithdraw?.count ?? 0}`}
          icon={<Report sx={{ fontSize: 16 }} aria-hidden />}
        />
        <div className="extract-period-summary__metric summary-card min-w-[140px] flex-1 shrink-0">
          <div className="flex min-w-0 items-center gap-2">
            <div className="summary-card-icon extract-period-summary__icon--pending">
              <HourglassBottom sx={{ fontSize: 16 }} aria-hidden />
            </div>
            <p className="min-w-0 flex-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
              {intlOr("baas.pld.cards.pending", "Pendentes")}
            </p>
          </div>
          <p className="mt-2 text-lg font-bold tabular-nums text-brand-dark dark:text-gray-100 sm:text-xl">
            {fromMajor(metricAmount(summary?.pending))}
          </p>
          <p className="mt-1 text-[0.7rem] text-gray-400">{summary?.pending?.count ?? 0}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Rail">
        {[
          { id: "pix", labelKey: "baas.pld.tabs.pix", fallback: "Pix" },
          { id: "stables", labelKey: "baas.pld.tabs.stables", fallback: "Stables" },
        ].map((tab) => {
          const active = railTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                  : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
              }`}
              onClick={() => setRailTab(tab.id)}
            >
              {intlOr(tab.labelKey, tab.fallback)}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Status">
          {STATUS_TABS.map((tab) => {
            const active = statusTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
                }`}
                onClick={() => setStatusTab(tab.id)}
              >
                {intlOr(tab.labelKey, tab.fallback)}
              </button>
            );
          })}
        </div>
        <select
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          className="baas-period-select !h-9 !py-1 text-xs"
          aria-label="Filtrar motivo"
        >
          {REASON_FILTERS.map((opt) => (
            <option key={opt.id || "all"} value={opt.id}>{opt.label}</option>
          ))}
        </select>
      </div>

      <GridHistoryContainer gridCols={GRID_HISTORY_COLS_8}>
        <GridHistoryHeader colSpan={8}>
          <span>{intlOr("baas.pld.columns.when", "Quando")}</span>
          <span>{intlOr("baas.pld.columns.entity", "Entidade")}</span>
          <span>{intlOr("baas.pld.columns.reason", "Motivo")}</span>
          <span>{intlOr("baas.pld.columns.payer", "Pagador")}</span>
          <span>{intlOr("baas.pld.columns.e2e", "E2E")}</span>
          <span>{intlOr("baas.pld.columns.period", "Período")}</span>
          <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>
            {intlOr("baas.pld.columns.amount", "Valor")}
          </span>
          <GridHistoryStatusActionsHeader
            statusLabel={intlOr("baas.pld.columns.status", "Status")}
          />
        </GridHistoryHeader>
        {paginatedItems.length === 0 ? (
          <p className="col-span-8 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {intlOr("baas.pld.empty", "Nenhum hold encontrado.")}
          </p>
        ) : (
          paginatedItems.map((item) => (
            <GridHistoryRow key={item.id} colSpan={8}>
              <GridHistoryCell label={intlOr("baas.pld.columns.when", "Quando")}>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(item.created_at ?? item.createdAt)}
                </p>
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.entity", "Entidade")}>
                <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                  {item.entity_name || item.entityName || item.account_id || item.accountId || "—"}
                </p>
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.reason", "Motivo")}>
                <ReasonBadge reason={item.reason_code ?? item.reasonCode} />
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.payer", "Pagador")}>
                <p className="truncate text-sm text-brand-dark dark:text-gray-100">
                  {item.payer_name ?? item.payerName ?? "—"}
                </p>
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.e2e", "E2E")}>
                <p className="truncate font-mono text-xs text-gray-500">
                  {item.external_ref ?? item.externalRef ?? "—"}
                </p>
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.period", "Período")}>
                <p className="text-sm text-brand-dark dark:text-gray-100">
                  {item.period_type ?? item.periodType ?? "—"}
                </p>
              </GridHistoryCell>
              <GridHistoryCell label={intlOr("baas.pld.columns.amount", "Valor")} align="right">
                <p className="text-sm font-semibold tabular-nums text-brand-dark dark:text-gray-100">
                  {fromMajor(item.amount)}
                </p>
              </GridHistoryCell>
              <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
                <p className="mb-px w-full text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intlOr("baas.pld.columns.status", "Status")}
                </p>
                <PldStatusBadge status={item.status} />
                <GridHistoryDetailButton onClick={() => setDetailItem(item)} />
                {canAct ? (
                  <BaasRowActionMenu
                    items={[
                      {
                        id: "approve",
                        label: intlOr("baas.pld.approve", "Aprovar"),
                        onClick: () => act(item.id, "approve"),
                        disabled:
                          Boolean(actingId) || String(item.status).toLowerCase() !== "pending",
                      },
                      {
                        id: "reject",
                        label: intlOr("baas.pld.reject", "Rejeitar"),
                        onClick: () => act(item.id, "reject"),
                        disabled:
                          Boolean(actingId) || String(item.status).toLowerCase() !== "pending",
                        danger: true,
                      },
                    ]}
                  />
                ) : null}
              </div>
            </GridHistoryRow>
          ))
        )}
      </GridHistoryContainer>

      <GridPagination
        total={items.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(1);
        }}
        currentPageCount={paginatedItems.length}
        itemLabel={intlOr("baas.pld.paginationLabel", "holds")}
      />

      <BaasPldDetailDrawer item={detailItem} onClose={() => setDetailItem(null)} />
    </div>
  );
};
